/**
 * Lead Detail Page
 * 
 * Displays comprehensive lead information with edit and conversion capabilities
 * 
 * Requirements: 9.1, 9.2, 9.3, 9.4, 9.5, 10.1, 10.2
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  PersonAdd as ConvertIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { leadService } from '../services/leadService';
import { Lead, LeadStatus, LeadSource, GSTVerificationStatus } from '../types/lead';
import GSTVerificationBadge from '../components/leads/GSTVerificationBadge';
import CampaignTagManager from '../components/leads/CampaignTagManager';

const LeadDetailPage: React.FC = () => {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  
  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (leadId) {
      loadLead();
    }
  }, [leadId]);

  const loadLead = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await leadService.getById(leadId!);
      setLead(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load lead');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/admin/leads/${leadId}/edit`);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this lead?')) {
      return;
    }
    
    try {
      await leadService.delete(leadId!);
      navigate('/admin/leads');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete lead');
    }
  };

  const handleConvertToCustomer = () => {
    // Navigate to customer creation with pre-filled data
    navigate('/admin/customers/create', {
      state: {
        fromLead: lead,
      },
    });
  };

  const handleBack = () => {
    navigate('/admin/leads');
  };

  const getStatusColor = (status: LeadStatus): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (status) {
      case LeadStatus.NEW:
        return 'primary';
      case LeadStatus.CONTACTED:
        return 'default';
      case LeadStatus.QUALIFIED:
        return 'warning';
      case LeadStatus.CONVERTED:
        return 'success';
      case LeadStatus.LOST:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: LeadStatus): string => {
    switch (status) {
      case LeadStatus.NEW:
        return 'New';
      case LeadStatus.CONTACTED:
        return 'Contacted';
      case LeadStatus.QUALIFIED:
        return 'Qualified';
      case LeadStatus.CONVERTED:
        return 'Converted';
      case LeadStatus.LOST:
        return 'Lost';
      default:
        return status;
    }
  };

  const getSourceLabel = (source: LeadSource): string => {
    switch (source) {
      case LeadSource.INDIAMART:
        return 'Indiamart';
      case LeadSource.INSTAGRAM:
        return 'Instagram';
      case LeadSource.FACEBOOK_ADS:
        return 'Facebook Ads';
      case LeadSource.MANUAL_ENTRY:
        return 'Manual Entry';
      default:
        return source;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !lead) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || 'Lead not found'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          Back to Leads
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">Lead Details</Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          {lead.status !== LeadStatus.CONVERTED && (
            <Button
              variant="contained"
              color="success"
              startIcon={<ConvertIcon />}
              onClick={handleConvertToCustomer}
            >
              Convert to Customer
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {lead.name}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Company
                  </Typography>
                  <Typography variant="body1">
                    {lead.company || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body1">
                    {lead.phone}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {lead.email || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      size="small"
                      label={getStatusLabel(lead.status)}
                      color={getStatusColor(lead.status)}
                    />
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Source
                  </Typography>
                  <Typography variant="body1">
                    {getSourceLabel(lead.source)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Location & GST Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Location & GST Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="body1">
                    {lead.location || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    City
                  </Typography>
                  <Typography variant="body1">
                    {lead.city || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    State
                  </Typography>
                  <Typography variant="body1">
                    {lead.state || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    GSTIN
                  </Typography>
                  <Typography variant="body1">
                    {lead.gst_number || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    GST Verification Status
                  </Typography>
                  <Box mt={0.5}>
                    <GSTVerificationBadge
                      status={lead.gst_verification_status}
                      verifiedAt={lead.gst_verified_at}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Interest & Query */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Interest & Query Details
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Interest Category
                  </Typography>
                  <Typography variant="body1">
                    {lead.interest_category || '-'}
                  </Typography>
                </Grid>

                {/* Parse and highlight Monthly Requirement from notes */}
                {lead.notes && (() => {
                  const match = lead.notes.match(/Monthly Requirement:\s*([\d,]+)/);
                  if (match) {
                    return (
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">
                          Monthly Requirement (Bulk Order)
                        </Typography>
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center',
                          bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200',
                          borderRadius: 2, px: 2, py: 0.75, mt: 0.5
                        }}>
                          <Typography variant="h6" color="success.700" fontWeight={800}>
                            ₹{parseInt(match[1].replace(/,/g, '')).toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="caption" color="success.600" sx={{ ml: 1 }}>/ month</Typography>
                        </Box>
                      </Grid>
                    );
                  }
                  return null;
                })()}

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Notes / Query Details
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                    {lead.notes || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Campaign Tags */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Campaign Tags
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <CampaignTagManager
                tags={lead.campaign_tags || []}
                onChange={async (newTags) => {
                  try {
                    // Update tags via API
                    await leadService.update(lead.id, { campaign_tags: newTags });
                    // Reload lead data
                    loadLead();
                  } catch (err: any) {
                    setError(err.response?.data?.detail || 'Failed to update tags');
                  }
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Additional Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Additional Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body1">
                    {new Date(lead.created_at).toLocaleString()}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Updated At
                  </Typography>
                  <Typography variant="body1">
                    {new Date(lead.updated_at).toLocaleString()}
                  </Typography>
                </Grid>

              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LeadDetailPage;
